import { DEFAULT_ESTIMATE_ROOM } from "./constants.js";
/**
 * Which of the three sequential (non-transactional) bootstrap writes failed.
 * Callers use this to classify a raw Postgres/Supabase error into a stable,
 * safe diagnostic code instead of the generic "something went wrong" — and,
 * critically, to know how much of the bootstrap already persisted before the
 * failure (these writes are not wrapped in a transaction/RPC).
 */
export const ESTIMATE_BOOTSTRAP_STAGE = Object.freeze({
    HEADER: "header",
    DEFAULT_ROOM: "default_room",
    EVENT: "event",
});
function tagBootstrapStageError(error, stage, context) {
    if (error && typeof error === "object") {
        Object.assign(error, {
            estimateBootstrapStage: stage,
            estimateBootstrapContext: context,
        });
    }
    return error;
}
/**
 * Canonical draft estimate bootstrap: estimates → default room → created event.
 * Used by CrewPilot UI and must mirror the Phase 2 mapping RPC sequence.
 *
 * NOT ATOMIC: each INSERT is a separate round trip. A failure on the 2nd or
 * 3rd step leaves the earlier row(s) already persisted — see
 * ESTIMATE_BOOTSTRAP_STAGE and the context attached to the thrown error for
 * exactly what already exists when this throws.
 */
export async function createDraftEstimate(client, params) {
    const insertPayload = {
        organization_id: params.organizationId,
        client_id: params.clientId,
        estimate_number: params.estimateNumber,
        title: params.title,
        status: "draft",
        created_by: params.createdBy ?? null,
        updated_by: params.updatedBy ?? null,
    };
    if (params.siteAddress) {
        insertPayload.site_address = params.siteAddress;
    }
    if (params.notes) {
        insertPayload.notes = params.notes;
    }
    if (params.internalNotes) {
        insertPayload.internal_notes = params.internalNotes;
    }
    if (params.sourceIntakeId) {
        insertPayload.source_intake_id = params.sourceIntakeId;
    }
    const { data, error } = await client
        .from("estimates")
        .insert(insertPayload)
        .select("estimate_id")
        .single();
    if (error) {
        throw tagBootstrapStageError(error, ESTIMATE_BOOTSTRAP_STAGE.HEADER, {
            organizationId: params.organizationId,
            clientId: params.clientId,
        });
    }
    const { data: room, error: roomError } = await client
        .from("estimate_rooms")
        .insert({
        estimate_id: data.estimate_id,
        name: DEFAULT_ESTIMATE_ROOM.name,
        room_type: DEFAULT_ESTIMATE_ROOM.roomType,
        sort_order: DEFAULT_ESTIMATE_ROOM.sortOrder,
        is_default_scope: DEFAULT_ESTIMATE_ROOM.isDefaultScope,
    })
        .select("room_id")
        .single();
    if (roomError) {
        // Header row already persisted — this is a partial create.
        throw tagBootstrapStageError(roomError, ESTIMATE_BOOTSTRAP_STAGE.DEFAULT_ROOM, {
            organizationId: params.organizationId,
            clientId: params.clientId,
            estimateId: data.estimate_id,
        });
    }
    const { error: eventError } = await client.from("estimate_events").insert({
        estimate_id: data.estimate_id,
        organization_id: params.organizationId,
        event_type: "created",
        to_status: "draft",
        actor_id: params.createdBy ?? null,
        payload: params.eventPayload ?? { source: "estimate_bootstrap" },
    });
    if (eventError) {
        // Header + default room already persisted — this is a partial create.
        throw tagBootstrapStageError(eventError, ESTIMATE_BOOTSTRAP_STAGE.EVENT, {
            organizationId: params.organizationId,
            clientId: params.clientId,
            estimateId: data.estimate_id,
            defaultRoomId: room.room_id,
        });
    }
    return {
        estimateId: data.estimate_id,
        defaultRoomId: room.room_id,
    };
}
